// =============================================================================
// AcceptInvite.js, /invite and /invite/:token
//
// Where partners land after the inviter shares an email link (token) or a
// 6-character code. The page:
//   1. Requires the user to be signed in (redirects to /signup otherwise,
//      preserving the token in state so we can complete the flow post-auth).
//   2. Calls /api/workspaces/accept with token or code.
//   3. On success: toast + redirect to /dashboard.
// =============================================================================
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Sparkles, KeyRound, Check, AlertCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { acceptInvite } from '@/lib/workspaces';
import usePageTitle from '@/hooks/usePageTitle';

export default function AcceptInvite() {
  usePageTitle('Accept invite');
  const { token: paramToken } = useParams();
  const [searchParams] = useSearchParams();
  const queryToken = searchParams.get('token');
  const incomingToken = paramToken || queryToken;

  const { user, session, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState(null); // 'success' | 'error'
  const [resultMsg, setResultMsg] = useState('');

  const tryAccept = useCallback(async (opts) => {
    if (!session?.access_token) return;
    setWorking(true);
    const res = await acceptInvite(session.access_token, opts);
    setWorking(false);
    if (res?.ok) {
      setResult('success');
      setResultMsg('You’re in, welcome to your partner’s shared workspace.');
      toast.success('Joined the workspace');
      try { await refreshProfile?.(); } catch (e) { /* ignore */ }
      setTimeout(() => navigate('/dashboard'), 1500);
    } else {
      setResult('error');
      setResultMsg(res?.error || 'Could not accept this invite.');
    }
  }, [session?.access_token, navigate, refreshProfile]);

  // Auto-redeem on mount if token is in URL.
  useEffect(() => {
    if (!user) return; // wait for auth
    if (incomingToken && result === null) {
      tryAccept({ token: incomingToken });
    }
  }, [user, incomingToken, result, tryAccept]);

  // Auth gate
  if (!user) {
    return (
      <div className="min-h-screen grid place-items-center bg-background p-4">
        <Card className="max-w-md w-full rounded-2xl">
          <CardContent className="p-6 sm:p-8 text-center">
            <Sparkles className="h-6 w-6 text-[hsl(var(--gold))] mx-auto" />
            <h1 className="font-display text-2xl font-semibold tracking-tight mt-3">Accept your workspace invite</h1>
            <p className="text-sm text-muted-foreground mt-2">Sign in or create your free 2120 account first, then we’ll bring you straight back here.</p>
            <div className="mt-6 flex gap-2 justify-center">
              <Button onClick={() => navigate(`/signup?next=${encodeURIComponent(window.location.pathname + window.location.search)}`)} className="rounded-xl" data-testid="invite-signup-cta">Create account</Button>
              <Button variant="outline" onClick={() => navigate(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`)} className="rounded-xl" data-testid="invite-login-cta">Sign in</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-background p-4">
      <Card className="max-w-md w-full rounded-2xl">
        <CardContent className="p-6 sm:p-8">
          {result === 'success' ? (
            <div className="text-center py-6" data-testid="invite-success">
              <div className="h-12 w-12 mx-auto rounded-full bg-primary/10 grid place-items-center">
                <Check className="h-6 w-6 text-primary" />
              </div>
              <h1 className="font-display text-2xl font-semibold tracking-tight mt-4">{resultMsg}</h1>
              <p className="text-sm text-muted-foreground mt-2">Taking you to your shared dashboard…</p>
            </div>
          ) : result === 'error' ? (
            <div className="text-center py-6" data-testid="invite-error">
              <div className="h-12 w-12 mx-auto rounded-full bg-destructive/10 grid place-items-center">
                <AlertCircle className="h-6 w-6 text-destructive" />
              </div>
              <h1 className="font-display text-xl font-semibold tracking-tight mt-4">We couldn’t accept this invite</h1>
              <p className="text-sm text-muted-foreground mt-2">{resultMsg}</p>
              <Button onClick={() => { setResult(null); setResultMsg(''); }} variant="outline" className="rounded-xl mt-4">Try a different code</Button>
            </div>
          ) : incomingToken ? (
            <div className="text-center py-6">
              <div className="h-12 w-12 mx-auto rounded-full bg-accent grid place-items-center animate-pulse">
                <KeyRound className="h-6 w-6 text-muted-foreground" />
              </div>
              <h1 className="font-display text-xl font-semibold tracking-tight mt-4">Accepting your invite…</h1>
            </div>
          ) : (
            <div>
              <div className="text-center">
                <Sparkles className="h-6 w-6 text-[hsl(var(--gold))] mx-auto" />
                <h1 className="font-display text-2xl font-semibold tracking-tight mt-3">Enter your invite code</h1>
                <p className="text-sm text-muted-foreground mt-2">Your partner generated a 6-character code. Paste it below to join their workspace.</p>
              </div>
              <div className="mt-6 space-y-3">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="ABC123"
                  maxLength={6}
                  className="text-center text-lg tracking-[0.4em] font-mono uppercase"
                  data-testid="invite-code-input"
                />
                <Button
                  onClick={() => code.length === 6 && tryAccept({ code })}
                  disabled={code.length !== 6 || working}
                  className="w-full rounded-xl"
                  data-testid="invite-code-submit"
                >
                  {working ? 'Accepting…' : 'Accept invite'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
